import { consumeReturnPath } from '../lib/workspaceStorage';
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import GoogleSignIn from '../components/GoogleSignIn';
import { APP_TAGLINE, roleHome, ORGANIZER_ONBOARDING, organizerNeedsOnboarding } from '../config/brand';
import axios from 'axios';

const Login = () => {
  const location = useLocation();
  const individualLogin = new URLSearchParams(location.search).get('role') === 'individual';
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const routeAfterLogin = async (role) => {
    if (role === 'venue') {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/venues/me`, { timeout: 15000 });
        return navigate(data ? consumeReturnPath() || roleHome(role) : '/venue/profile', { replace: true });
      } catch { return navigate('/venue/profile', { replace: true }); }
    }
    if (role === 'individual') return navigate('/individual', { replace: true });
    if (role !== 'organizer') return navigate(consumeReturnPath() || roleHome(role), { replace: true });
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizers/me`, { timeout: 15000 });
      navigate(organizerNeedsOnboarding(data) ? ORGANIZER_ONBOARDING : consumeReturnPath() || roleHome(role));
    } catch {
      navigate(roleHome(role));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      await routeAfterLogin(result.user?.role);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const quickLogin = async (demoEmail, demoPassword) => {
    setError('');
    setLoading(true);
    setEmail(demoEmail);
    setPassword(demoPassword);
    const result = await login(demoEmail, demoPassword);
    if (result.success) {
      await routeAfterLogin(result.user?.role);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6 py-10">
      <div className="w-full max-w-sm animate-soft-lift">
        <div className="mb-6 flex flex-col items-center text-center">
          <h1 className="text-3xl tracking-tight" data-testid="login-title">
            <Logo variant="wordmark" size="xl" />
          </h1>
          <p className="mt-2 text-sm text-[#475467]" data-testid="login-tagline">
            {individualLogin ? "Meko'yla planına kaldığın yerden devam et." : APP_TAGLINE}
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4 bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm">
          <div>
            <label className="block text-sm text-zinc-600 mb-1.5" htmlFor="email">E-posta</label>
            <input
              id="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-xl bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
              placeholder="ornek@firma.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-600 mb-1.5" htmlFor="password">Şifre</label>
            <div className="relative">
              <input
                id="password"
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-4 pr-11 py-2.5 border border-zinc-200 rounded-xl bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-400 hover:text-zinc-700"
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                data-testid="login-password-toggle"
                
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Link to="/forgot-password" className="block text-right text-sm text-[#1E3A8A] hover:underline" data-testid="forgot-password-link">Şifremi unuttum</Link>

          {error && (
            <div
              className="px-4 py-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded-xl"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#FF7A00] hover:bg-[#E56D00] text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition-all duration-300 shadow-sm hover:shadow active:scale-[0.98]"
            data-testid="login-submit-button"
          >
            {loading ? 'Yükleniyor…' : 'Giriş Yap'}
          </button>
        </form>
        {!individualLogin && <GoogleSignIn disabled={loading} />}
        {!individualLogin && <section className="mt-5 rounded-2xl border border-[#CBD9F1] bg-[#F2F6FF] p-5" data-testid="individual-meko-entry">
          <p className="flex items-center gap-2 text-[11px] font-bold tracking-wider uppercase text-[#1E3A8A]"><Sparkles size={15} aria-hidden="true" /> Bireysel kullanım</p>
          <h2 className="mt-2 text-lg font-semibold text-[#17233C]">Bir planın mı var? Meko'ya anlat.</h2>
          <p className="text-sm text-[#5B677C] mt-1">Doğum günü, buluşma veya toplantı için mekânı sesle ya da yazarak bul.</p>
          <Link to="/bireysel/kayit" className="mt-4 min-h-11 px-4 py-3 rounded-xl bg-[#1E3A8A] text-white text-sm font-semibold inline-flex w-full items-center justify-center gap-2" data-testid="individual-meko-start">
            Meko'yla planla <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <p className="mt-2 text-xs text-[#5B677C]">Bireysel hesabın varsa yukarıdan giriş yapabilirsin.</p>
        </section>}

        {process.env.REACT_APP_ENABLE_DEMO_LOGIN === 'true' && (
        <div className="mt-6 rounded-2xl border border-dashed border-zinc-200 bg-white/60 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 text-center font-medium">
            Demo — tek tıkla giriş
          </p>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => quickLogin('admin@mekanmatch.com', 'admin123')}
              disabled={loading}
              className="py-2 text-xs font-medium rounded-lg bg-[#1E3A8A] text-white hover:bg-[#16307A] disabled:opacity-50 transition-colors"
              data-testid="demo-login-admin"
            >
              Admin
            </button>
            <button
              type="button"
              onClick={() => quickLogin('organizator1@test.com', 'organizator123')}
              disabled={loading}
              className="py-2 text-xs font-medium rounded-lg bg-zinc-100 text-zinc-800 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
              data-testid="demo-login-organizer"
            >
              Organizatör
            </button>
            <button
              type="button"
              onClick={() => quickLogin('mekan1@test.com', 'mekan123')}
              disabled={loading}
              className="py-2 text-xs font-medium rounded-lg bg-zinc-100 text-zinc-800 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
              data-testid="demo-login-venue"
            >
              Mekân
            </button>
          </div>
          <p className="mt-2 text-[10px] text-zinc-400 text-center leading-relaxed">
            admin123 · organizator123 · mekan123
          </p>
        </div>
        )}

        <p className="mt-6 text-center text-sm text-zinc-500">
          {individualLogin ? 'Henüz bireysel hesabın yok mu?' : 'Hesabınız yok mu?'}{' '}
          <Link
            to={individualLogin ? '/bireysel/kayit' : '/register'}
            className="text-[#1E3A8A] hover:text-[#16307A] hover:underline font-medium"
            data-testid="login-register-link"
          >
            Kayıt Ol
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;


